import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { getEncounterById } from "redux-store/features/Encounter";
import { addBreadcrumb } from "services/monitoring";
import { useWebSocketEvent } from "./useWebSocketEvent";

/**
 * Encounter processing message details
 */
interface EncounterProcessingDetails {
  patient_id?: string | number;
  encounter_recording_id?: string;
  state?: "started" | "finished" | "failed";
}

/**
 * Encounter processing WebSocket message
 */
interface EncounterProcessingMessage {
  event_type: "encounter_processing_status";
  account_id?: string | number;
  details?: EncounterProcessingDetails;
}

/**
 * History entry structure for Smart Summary refresh
 * Matches backend SmartSummaryHistoryStep struct
 */
interface HistoryEntry {
  status: string;
  timestamp: string;
  duration_ms?: number;
  [key: string]: any;
}

/**
 * Smart summary status message details
 * Matches backend SmartSummaryEventDetail struct
 *
 * NEW STRUCTURE (after backend update):
 * - patient_id: Now inside details object (not top-level)
 * - status: Overall status (e.g., "started", "finished")
 * - process_id: Background process ID
 * - current_step: Current step being executed
 * - history: Array of all steps with timestamps
 */
interface SmartSummaryStatusDetails {
  patient_id?: string | number; // NOW IN DETAILS (moved from top-level)
  status?: string; // Overall status
  process_id?: string; // NEW: Background process ID
  current_step?: string; // NEW: Current step name
  history?: HistoryEntry[]; // Array of history steps
  [key: string]: any;
}

/**
 * Smart summary status WebSocket message
 *
 * STRUCTURE CHANGE:
 * - OLD: patient_id was at top level alongside details
 * - NEW: patient_id is inside details object
 */
interface SmartSummaryStatusMessage {
  event_type: "smart_summary_status";
  account_id?: string | number;
  details?: SmartSummaryStatusDetails;
}

/**
 * Room number update message details
 */
interface RoomNumberUpdateDetails {
  patient_id?: string;
  room_number?: string;
}

/**
 * Room number update WebSocket message
 */
interface RoomNumberUpdateMessage {
  event_type: "room_number_updated";
  details?: RoomNumberUpdateDetails;
}

/**
 * PIF completion status update message details
 */
interface PIFCompletionStatusDetails {
  patient_id?: string;
  updated_value?: boolean;
}

/**
 * PIF completion status WebSocket message
 */
interface PIFCompletionStatusMessage {
  event_type: "pif_completion_status_updated";
  details?: PIFCompletionStatusDetails;
}

/**
 * Configuration for the unified WebSocket hook
 */
interface UseWebSocketHooksConfig {
  // Encounter processing
  patientId?: string | number;
  accountId?: string | number;
  encounterRecordingId?: string;
  onEncounterProcessingComplete?: (encounterRecordingId: string) => void;
  onEncounterProcessingStarted?: () => void;
  onEncounterProcessingFailed?: () => void;

  // Smart summary status
  onSmartSummaryRefresh?: (message: SmartSummaryStatusMessage) => void;

  // Room number updates
  onRoomNumberUpdate?: (message: RoomNumberUpdateMessage) => void;

  // PIF completion status updates
  onPIFCompletionStatusUpdate?: (message: PIFCompletionStatusMessage) => void;
}

/**
 * Unified WebSocket hook that handles all event types
 */
export const useWebSocketHooks = (config: UseWebSocketHooksConfig): void => {
  const {
    // Encounter processing
    patientId,
    accountId,
    encounterRecordingId,
    onEncounterProcessingComplete,
    onEncounterProcessingStarted,
    onEncounterProcessingFailed,

    // Smart summary status
    onSmartSummaryRefresh,

    // Room number updates
    onRoomNumberUpdate,

    // PIF completion status updates
    onPIFCompletionStatusUpdate,
  } = config;

  const dispatch = useDispatch();
  const processedEncounterIds = useRef<Set<string>>(new Set());

  // Global message validation helpers
  const isMessageForAccount = (message: { account_id?: string | number }): boolean => {
    return Number(message.account_id) === Number(accountId);
  };

  const isMessageForPatient = (message: { details?: { patient_id?: string | number } }): boolean => {
    return Number(message.details?.patient_id) === Number(patientId);
  };

  // Encounter processing event handler (handles started, finished, and failed states)
  const handleEncounterProcessingEvent = (message: EncounterProcessingMessage): void => {
    const accountMatch = isMessageForAccount(message);
    const patientMatch = isMessageForPatient(message);
    const encounterMatch = String(message.details?.encounter_recording_id) === String(encounterRecordingId);

    if (accountMatch && patientMatch && encounterMatch) {
      const state = message.details?.state;

      addBreadcrumb({
        category: "ws.processing",
        message: `encounter_processing_status: ${state}`,
        data: {
          state,
          encounter_recording_id: message.details?.encounter_recording_id,
        },
        level: state === "failed" ? "error" : "info",
      });

      if (state === "started") {
        if (onEncounterProcessingStarted) {
          onEncounterProcessingStarted();
        }
      } else if (state === "finished") {
        const notProcessed = encounterRecordingId && !processedEncounterIds.current.has(encounterRecordingId);

        if (notProcessed && encounterRecordingId) {
          processedEncounterIds.current.add(encounterRecordingId);
          dispatch(getEncounterById(encounterRecordingId));

          if (onEncounterProcessingComplete) {
            onEncounterProcessingComplete(encounterRecordingId);
          }
        }
      } else if (state === "failed") {
        // Remove from processedEncounterIds to allow retry
        if (encounterRecordingId) {
          processedEncounterIds.current.delete(encounterRecordingId);
          dispatch(getEncounterById(encounterRecordingId));
        }

        if (onEncounterProcessingFailed) {
          onEncounterProcessingFailed();
        }
      }
    }
  };

  // Smart summary status handler
  const handleSmartSummaryStatus = (message: SmartSummaryStatusMessage): void => {
    if (isMessageForAccount(message) && isMessageForPatient(message)) {
      const details = message.details;
      const historyData = (details?.history ?? []).map((entry) => ({
        status: entry.status,
        duration_ms: entry.duration_ms,
      }));

      addBreadcrumb({
        category: "ws.smart_summary",
        message: `smart_summary_status: ${details?.status ?? "unknown"} — ${details?.current_step ?? ""}`,
        data: {
          status: details?.status,
          current_step: details?.current_step,
          process_id: details?.process_id,
          history: historyData,
        },
        level: details?.status === "failed" ? "error" : "info",
      });

      if (onSmartSummaryRefresh) {
        onSmartSummaryRefresh(message);
      }
    }
  };

  // Room number update handler
  const handleRoomNumberUpdate = (message: RoomNumberUpdateMessage): void => {
    if (onRoomNumberUpdate) {
      onRoomNumberUpdate(message);
    }
  };

  // PIF completion status update handler
  const handlePIFCompletionStatusUpdate = (message: PIFCompletionStatusMessage): void => {
    addBreadcrumb({
      category: "ws.pif",
      message: `pif_completion_status_updated`,
      data: {
        updated_value: message.details?.updated_value,
      },
      level: "info",
    });

    if (onPIFCompletionStatusUpdate) {
      onPIFCompletionStatusUpdate(message);
    }
  };

  useWebSocketEvent<EncounterProcessingMessage>("encounter_processing_status", handleEncounterProcessingEvent);
  useWebSocketEvent<SmartSummaryStatusMessage>("smart_summary_status", handleSmartSummaryStatus);
  useWebSocketEvent<RoomNumberUpdateMessage>("room_number_updated", handleRoomNumberUpdate);
  useWebSocketEvent<PIFCompletionStatusMessage>("pif_completion_status_updated", handlePIFCompletionStatusUpdate);

  // Cleanup on unmount
  useEffect(() => {
    const currentProcessedIds = processedEncounterIds.current;
    return () => {
      currentProcessedIds.clear();
    };
  }, []);
};
